import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  Menu,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function VettingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [action, setAction] = useState('schedule_interview');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTutor, setMenuTutor] = useState(null);
  const [form, setForm] = useState({
    interviewDate: '',
    interviewTime: '',
    interviewMode: 'online',
    interviewLocation: '',
    notes: '',
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/admin/vetting-requests`);
      setRequests(res.data || []);
    } catch (error) {
      console.error('Failed to load vetting requests:', error);
      setSnackbar({ open: true, message: 'Failed to load vetting requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const pendingCount = useMemo(() => requests.filter((tutor) => tutor.vetting_status === 'pending').length, [requests]);

  const isVetted = (tutor) => tutor?.tutor_status === 'vetted';
  const isMarketplaceActive = (tutor) => Number(tutor?.is_verified) === 1;
  const canFail = (tutor) => tutor && tutor.vetting_status !== 'passed';

  const handleOpenDialog = (tutor, nextAction = 'schedule_interview') => {
    setSelectedTutor(tutor);
    setAction(nextAction);
    setForm({ interviewDate: '', interviewTime: '', interviewMode: 'online', interviewLocation: '', notes: '' });
    setDialogOpen(true);
  };

  const handleMenuOpen = (event, tutor) => {
    setMenuAnchor(event.currentTarget);
    setMenuTutor(tutor);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTutor(null);
  };

  const handleSubmit = async () => {
    if (!selectedTutor) return;

    try {
      await axios.put(`${BASE_URL}/api/admin/vetting-requests/${selectedTutor.request_id}`, {
        action,
        ...form,
      });

      setSnackbar({
        open: true,
        message: 'Vetting request updated successfully.',
        severity: 'success',
      });
      setDialogOpen(false);
      setSelectedTutor(null);
      fetchRequests();
    } catch (error) {
      console.error('Failed to update vetting request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update vetting request',
        severity: 'error',
      });
    }
  };

  const handleQuickAction = async (requestId, nextAction) => {
    try {
      await axios.put(`${BASE_URL}/api/admin/vetting-requests/${requestId}`, { action: nextAction });
      setSnackbar({ open: true, message: 'Vetting request updated successfully.', severity: 'success' });
      handleMenuClose();
      fetchRequests();
    } catch (error) {
      console.error('Failed to update vetting request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update vetting request',
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'passed': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getTutorStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'vetted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header Section */}
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#1B1C1E' }}>
              Tutor Vetting
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Review and manage tutor vetting requests
            </Typography>
          </Box>
          <Chip 
            icon={<PendingActionsIcon />} 
            label={`${pendingCount} Pending`} 
            color="warning" 
            sx={{ 
              fontWeight: 600,
              px: 2,
              py: 2.5,
              '& .MuiChip-label': { fontSize: '0.9rem' }
            }}
          />
        </Box>
      </Stack>

      {/* Table Section */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#F8F9FA' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#1B1C1E', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Tutor
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1B1C1E', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1B1C1E', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Profile
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1B1C1E', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: '#1B1C1E', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
                        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #f3f3f3', borderTop: '3px solid #1976d2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <Typography color="text.secondary">Loading requests...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <PendingActionsIcon sx={{ fontSize: 48, color: '#ccc' }} />
                        <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          No vetting requests found
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          All tutor requests have been processed
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((tutor) => (
                    <TableRow 
                      key={tutor.request_id} 
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: '#F8F9FA' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography fontWeight={600} sx={{ color: '#1B1C1E' }}>
                            {tutor.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {tutor.location || 'Location not provided'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#1B1C1E' }}>
                            {tutor.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {tutor.phone || 'No phone provided'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 250 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1B1C1E' }}>
                            {tutor.degree || 'No degree listed'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tutor.bio || 'No bio submitted'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Chip 
                            size="small" 
                            label={tutor.vetting_status?.toUpperCase() || 'PENDING'} 
                            color={getStatusColor(tutor.vetting_status)}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                          <Chip 
                            size="small" 
                            label={tutor.tutor_status?.toUpperCase() || 'PENDING'} 
                            color={getTutorStatusColor(tutor.tutor_status)}
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Actions">
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, tutor)}
                            sx={{
                              color: '#6E6E70',
                              '&:hover': {
                                backgroundColor: '#F0F0F0',
                                color: '#1B1C1E'
                              }
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Kebab Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              gap: 1.5,
            }
          }
        }}
      >
        {menuTutor && isVetted(menuTutor) ? (
          // Marketplace toggle for vetted tutors
          <MenuItem
            onClick={() => {
              handleQuickAction(
                menuTutor.request_id, 
                isMarketplaceActive(menuTutor) ? 'deactivate_marketplace_access' : 'activate_marketplace_access'
              );
            }}
          >
            <ListItemIcon>
              {isMarketplaceActive(menuTutor) ? (
                <ToggleOffIcon color="error" />
              ) : (
                <ToggleOnIcon color="success" />
              )}
            </ListItemIcon>
            <ListItemText>
              {isMarketplaceActive(menuTutor) ? 'Deactivate Marketplace' : 'Activate Marketplace'}
            </ListItemText>
          </MenuItem>
        ) : (
          // Vetting actions for non-vetted tutors
          <>
            <MenuItem
              onClick={() => {
                if (menuTutor) handleOpenDialog(menuTutor, 'schedule_interview');
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <EventNoteIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Schedule Interview" />
              <Typography variant="caption" color="text.secondary">
                Set up meeting
              </Typography>
            </MenuItem>
            
            <Divider />
            
            <MenuItem
              onClick={() => {
                if (menuTutor) {
                  handleQuickAction(menuTutor.request_id, 'pass');
                }
              }}
              sx={{ color: 'success.main' }}
            >
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary="Approve Tutor" />
              <Typography variant="caption" color="text.secondary">
                Pass vetting
              </Typography>
            </MenuItem>
            
            <MenuItem
              disabled={menuTutor ? !canFail(menuTutor) : true}
              onClick={() => {
                if (menuTutor && canFail(menuTutor)) {
                  handleQuickAction(menuTutor.request_id, 'fail');
                }
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <CancelIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Reject Tutor" 
                secondary={!canFail(menuTutor) ? "Already approved" : ""}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Schedule Interview Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Schedule Interview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set up an interview for {selectedTutor?.name || 'the tutor'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              type="date"
              label="Interview Date"
              InputLabelProps={{ shrink: true }}
              value={form.interviewDate}
              onChange={(e) => setForm((prev) => ({ ...prev, interviewDate: e.target.value }))}
              fullWidth
              size="medium"
            />
            <TextField
              type="time"
              label="Interview Time"
              InputLabelProps={{ shrink: true }}
              value={form.interviewTime}
              onChange={(e) => setForm((prev) => ({ ...prev, interviewTime: e.target.value }))}
              fullWidth
              size="medium"
            />
            <FormControl fullWidth size="medium">
              <InputLabel>Mode</InputLabel>
              <Select
                value={form.interviewMode}
                label="Mode"
                onChange={(e) => setForm((prev) => ({ ...prev, interviewMode: e.target.value }))}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="physical">Physical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={form.interviewMode === 'physical' ? 'Interview Address' : 'Interview Link'}
              placeholder={form.interviewMode === 'physical' ? 'Enter the physical interview address' : 'Paste the meeting link here'}
              value={form.interviewLocation}
              onChange={(e) => setForm((prev) => ({ ...prev, interviewLocation: e.target.value }))}
              fullWidth
              size="medium"
            />
            <TextField
              label="Notes"
              multiline
              minRows={3}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              size="medium"
              placeholder="Add any additional notes or instructions..."
            />
            <Divider />
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                This action will keep the tutor in pending review until you mark them as approved or rejected.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            sx={{ 
              px: 3,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Schedule Interview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ alignItems: 'center' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* CSS animation for spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
}